import { useCallback } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';

/**
 * The workspace an organization has with one service type (Compute, Kubernetes, ...): the thing a console is
 * opened on. Each service package describes its own through a `WorkspaceService`; the app merges them
 * (`src/app/workspace-services.ts`) and everything that has to know "which workspace is this organization's" reads
 * the merged list through `useOrgWorkspaces` and `resolveOrganizationWorkspace`, never from a service's own API.
 */
export type WorkspaceScope = 'org' | 'projects' | 'resources';

export interface OrgWorkspace {
  serviceType: string;
  organizationId: string;
  /** The label the service gives the workspace (the organization's name as that service knows it). */
  organizationName: string;
  /** The person's own workspace with this service, as opposed to one another organization shared with them. */
  home: boolean;
  /**
   * What the service's URLs carry to name the workspace (Compute: its metadata id). `null` when the service has no
   * workspace in the URL (Kubernetes); an empty string is a workspace that cannot be opened.
   */
  ref: string | null;
  /** The whole organization (an organization-level role), some of its projects, or single resources only. */
  scope: WorkspaceScope;
  projectIds: string[];
  /** Machines (Compute) or clusters (Kubernetes) shared one by one. */
  resourceIds: string[];
}

/** Just enough of an HTTP client for an adapter: the app's clients satisfy it, a test passes a fake. */
export interface WorkspaceClient {
  get: (url: string) => Promise<{ data: unknown }>;
}

interface WorkspaceSource<T> {
  queryKey: readonly unknown[];
  fetch: () => Promise<T>;
  staleTime: number;
}

/**
 * One service type's adapter: plain data and async functions (no hooks), so a package can return it from its module
 * factory like `serviceScopes`. The two answers it reads are queries of their own so that an adapter can use the key
 * of a query the console already runs and cost no second fetch.
 */
export interface WorkspaceService {
  type: string;
  home: WorkspaceSource<unknown>;
  shared: WorkspaceSource<unknown>;
  homeEntries: (raw: unknown) => OrgWorkspace[];
  sharedEntries: (raw: unknown) => OrgWorkspace[];
  /** Where `page` of this workspace's console lives. */
  href: (workspace: OrgWorkspace, page: string) => string;
}

/** Types an adapter's two raw answers; the registry holds them as `WorkspaceService`. */
export function defineWorkspaceService<H, S>(def: {
  type: string;
  home: WorkspaceSource<H>;
  shared: WorkspaceSource<S>;
  homeEntries: (raw: H) => OrgWorkspace[];
  sharedEntries: (raw: S) => OrgWorkspace[];
  href: (workspace: OrgWorkspace, page: string) => string;
}): WorkspaceService {
  return {
    ...def,
    home: def.home as WorkspaceSource<unknown>,
    shared: def.shared as WorkspaceSource<unknown>,
    homeEntries: (raw) => def.homeEntries(raw as H),
    sharedEntries: (raw) => def.sharedEntries(raw as S),
  };
}

/** The one trim rule for an organization or workspace id: ids arrive as numbers, strings and padded strings. */
export function normalizeId(id: string | number | null | undefined): string {
  return id == null ? '' : String(id).trim();
}

/** Two ids name the same thing. An empty id names nothing, so it equals nothing. */
export function sameId(a: string | number | null | undefined, b: string | number | null | undefined): boolean {
  const x = normalizeId(a);
  return x !== '' && x === normalizeId(b);
}

type WorkspaceResolution<W = OrgWorkspace> = { kind: 'home' } | { kind: 'shared'; workspace: W } | { kind: 'none' };

type Resolvable = Pick<OrgWorkspace, 'serviceType' | 'organizationId' | 'home' | 'ref'>;

/**
 * Whose workspace of `serviceType` a thing of `organizationId` opens in. The same organization, or one that is not
 * named, is the person's home workspace; another organization is the workspace it shared, and never the home one
 * (that would show the wrong organization's data under a foreign URL): `none` when it shared none, or one that cannot
 * be opened (an empty `ref`; `null` is fine, the service has no workspace in its URLs).
 */
export function resolveOrganizationWorkspace<W extends Resolvable>(input: {
  serviceType: string;
  organizationId: string | number | null | undefined;
  homeOrganizationId: string | number | null | undefined;
  workspaces: readonly W[];
}): WorkspaceResolution<W> {
  const organization = normalizeId(input.organizationId);
  const home = normalizeId(input.homeOrganizationId);
  if (!organization || !home || organization === home) return { kind: 'home' };
  const workspace = input.workspaces.find(
    (w) =>
      w.serviceType === input.serviceType &&
      !w.home &&
      sameId(w.organizationId, organization) &&
      (w.ref === null || normalizeId(w.ref) !== ''),
  );
  return workspace ? { kind: 'shared', workspace } : { kind: 'none' };
}

interface OrgWorkspacesState {
  workspaces: OrgWorkspace[];
  /** Every answer is in (a failed one counts: see `failed`). */
  settled: boolean;
  /** Service types with an answer that failed; the others' workspaces are still listed. */
  failed: string[];
}

type SourceResult = Pick<UseQueryResult<unknown>, 'data' | 'isPending' | 'isError'>;

/** The merge `useOrgWorkspaces` makes of its queries (two per service, in registry order). */
export function combineWorkspaceResults(services: readonly WorkspaceService[], results: readonly SourceResult[]): OrgWorkspacesState {
  const workspaces: OrgWorkspace[] = [];
  const failed: string[] = [];
  let settled = true;
  services.forEach((service, i) => {
    const home = results[2 * i];
    const shared = results[2 * i + 1];
    if (home?.isPending || shared?.isPending) settled = false;
    if (home?.isError || shared?.isError) failed.push(service.type);
    if (home && !home.isError && !home.isPending) workspaces.push(...service.homeEntries(home.data));
    if (shared && !shared.isError && !shared.isPending) workspaces.push(...service.sharedEntries(shared.data));
  });
  return { workspaces, settled, failed };
}

/** The workspaces the person has or reaches, for every registered service type. */
export function useOrgWorkspaces(services: readonly WorkspaceService[]): OrgWorkspacesState {
  const combine = useCallback((results: UseQueryResult<unknown>[]) => combineWorkspaceResults(services, results), [services]);
  return useQueries({
    queries: services.flatMap((s) =>
      [s.home, s.shared].map((source) => ({
        queryKey: source.queryKey,
        queryFn: source.fetch,
        staleTime: source.staleTime,
        retry: false,
      })),
    ),
    combine,
  });
}
